import { cn } from '@/lib/utils'

export interface SkeletonProps extends React.ComponentProps<'div'> {
  /** Use shimmer animation instead of pulse */
  shimmer?: boolean
}

function Skeleton({ className, shimmer = false, ...props }: SkeletonProps) {
  return (
    <div
      data-slot="skeleton"
      className={cn(
        'bg-muted rounded-md',
        shimmer ? 'skeleton-shimmer' : 'animate-pulse',
        className
      )}
      {...props}
    />
  )
}

export interface TableSkeletonProps {
  /** Number of rows to display */
  rows?: number
  /** Number of columns to display */
  columns?: number
  /** Show table header skeleton */
  showHeader?: boolean
  /** Use shimmer animation */
  shimmer?: boolean
  /** Additional className */
  className?: string
}

function TableSkeleton({
  rows = 5,
  columns = 4,
  showHeader = true,
  shimmer = true,
  className,
}: TableSkeletonProps) {
  return (
    <div className={cn('w-full', className)} data-testid="table-skeleton">
      {showHeader && (
        <div className="border-b pb-3">
          <div className="flex gap-4">
            {Array.from({ length: columns }).map((_, i) => (
              <Skeleton
                key={`header-${i}`}
                shimmer={shimmer}
                className={cn('h-4', i === 0 ? 'w-32' : 'w-24')}
              />
            ))}
          </div>
        </div>
      )}
      <div className="space-y-3 pt-3">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={rowIndex} className="flex items-center gap-4">
            {Array.from({ length: columns }).map((_, colIndex) => (
              <Skeleton
                key={`${rowIndex}-${colIndex}`}
                shimmer={shimmer}
                className={cn(
                  'h-4',
                  colIndex === 0 ? 'w-32' : colIndex === columns - 1 ? 'w-16' : 'w-24'
                )}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

export interface ListSkeletonProps {
  /** Number of items to display */
  items?: number
  /** Show avatar/icon placeholder */
  showAvatar?: boolean
  /** Show secondary line */
  showSecondary?: boolean
  /** Use shimmer animation */
  shimmer?: boolean
  /** Additional className */
  className?: string
}

function ListSkeleton({
  items = 5,
  showAvatar = true,
  showSecondary = true,
  shimmer = true,
  className,
}: ListSkeletonProps) {
  return (
    <div className={cn('space-y-4', className)} data-testid="list-skeleton">
      {Array.from({ length: items }).map((_, i) => (
        <div key={i} className="flex items-start gap-3">
          {showAvatar && <Skeleton shimmer={shimmer} className="size-10 shrink-0 rounded-full" />}
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton shimmer={shimmer} className="h-4 w-3/4" />
            {showSecondary && <Skeleton shimmer={shimmer} className="h-3 w-1/2" />}
          </div>
        </div>
      ))}
    </div>
  )
}

export interface CardSkeletonProps {
  /** Show header section */
  showHeader?: boolean
  /** Show icon in header */
  showIcon?: boolean
  /** Number of content lines */
  lines?: number
  /** Use shimmer animation */
  shimmer?: boolean
  /** Additional className */
  className?: string
}

function CardSkeleton({
  showHeader = true,
  showIcon = true,
  lines = 2,
  shimmer = true,
  className,
}: CardSkeletonProps) {
  return (
    <div className={cn('bg-card rounded-lg border p-6', className)} data-testid="card-skeleton">
      {showHeader && (
        <div className="mb-4 flex items-center justify-between">
          <Skeleton shimmer={shimmer} className="h-5 w-32" />
          {showIcon && <Skeleton shimmer={shimmer} className="size-5" />}
        </div>
      )}
      <div className="space-y-2">
        {Array.from({ length: lines }).map((_, i) => (
          <Skeleton key={i} shimmer={shimmer} className={cn('h-4', i === 0 ? 'w-full' : 'w-3/4')} />
        ))}
      </div>
    </div>
  )
}

export interface ChartSkeletonProps {
  /** Height of the chart area */
  height?: number | string
  /** Use shimmer animation */
  shimmer?: boolean
  /** Additional className */
  className?: string
}

function ChartSkeleton({ height = 300, shimmer = true, className }: ChartSkeletonProps) {
  const heightStyle = typeof height === 'number' ? `${height}px` : height

  return (
    <div className={cn('w-full', className)} data-testid="chart-skeleton">
      {/* Y-axis labels */}
      <div className="flex gap-4">
        <div className="flex w-8 flex-col justify-between" style={{ height: heightStyle }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} shimmer={shimmer} className="h-3 w-8" />
          ))}
        </div>
        {/* Chart area */}
        <div className="flex-1">
          <Skeleton shimmer={shimmer} className="w-full" style={{ height: heightStyle }} />
        </div>
      </div>
      {/* X-axis labels */}
      <div className="mt-2 flex justify-between pl-12">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} shimmer={shimmer} className="h-3 w-10" />
        ))}
      </div>
    </div>
  )
}

export { Skeleton, TableSkeleton, ListSkeleton, CardSkeleton, ChartSkeleton }
